import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExternalLink, ImageOff, Pencil, Plus, Trash2 } from "lucide-react";
import swal from "sweetalert";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import { getBanners, deleteBanner } from "../../../redux/banners/action";
import HomePageTabs from "./HomePageTabs";
import { PLACEMENT_OPTIONS, SURFACE_OPTIONS } from "./constants";
import "../DynamicSections/HomePageCRM.css";
import "./Banners.css";

const PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 350;

const formatDateRange = (startsAt, endsAt) => {
  if (!startsAt && !endsAt) return "Always";
  const fmt = (raw) =>
    raw
      ? new Date(raw).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : "—";
  return `${fmt(startsAt)} - ${fmt(endsAt)}`;
};

const buildQueryParams = (search) => {
  const out = { page: Number(search.get("page")) || 1, per_page: PER_PAGE };
  const searchKey = search.get("search_key");
  if (searchKey) out.search_key = searchKey;
  const placement = search.get("placement");
  if (placement) out.placement = placement;
  const surface = search.get("surface");
  if (surface) out.surface = surface;
  const isActive = search.get("is_active");
  if (isActive) out.is_active = isActive;
  return out;
};

const BannersList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch] = useSearchParams();
  const { hasAction } = usePermissions();

  const params = useMemo(() => buildQueryParams(search), [search]);
  const editable = hasAction(MENU.HOME, PERMISSION.EDIT);

  const banners = useSelector(
    (state) => state.reusableSlice?.banners?.data || [],
  );
  const pagination = useSelector(
    (state) => state.reusableSlice?.banners?.pagination || null,
  );

  const [searchDraft, setSearchDraft] = useState(params.search_key || "");
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchBanners = useCallback(() => {
    dispatch(getBanners(params, setIsLoading));
  }, [dispatch, params]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchDraft.trim();
      const current = search.get("search_key") || "";
      if (trimmed === current) return;
      const next = new URLSearchParams(search);
      if (trimmed) next.set("search_key", trimmed);
      else next.delete("search_key");
      next.set("page", "1");
      setSearch(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setSearch(next);
  };

  const changePage = (page) => {
    const next = new URLSearchParams(search);
    next.set("page", String(page));
    setSearch(next);
  };

  const handleDelete = (banner) => {
    swal({
      title: "Delete banner",
      text: `Remove banner #${banner.id} from the ${banner.placement} zone?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((confirm) => {
      if (!confirm) return;
      dispatch(
        deleteBanner(
          banner.id,
          (val) => setDeletingId(val ? banner.id : null),
          fetchBanners,
        ),
      );
    });
  };

  const totalPages = pagination?.pages || 1;
  const startIndex = (params.page - 1) * PER_PAGE;

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div>
          <h4>Home Page</h4>
          <p>Banners across the top hero, middle strip, and pre-footer zones.</p>
        </div>
        {editable && (
          <button className="ds-btn-primary" onClick={() => navigate("/banners/create")}>
            <Plus size={16} /> New Banner
          </button>
        )}
      </div>

      <HomePageTabs />

      <div className="ds-filters">
        <input
          type="text"
          placeholder="Search by link or image URL..."
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          style={{ width: 260 }}
        />
        <select
          value={params.placement || ""}
          onChange={(e) => updateFilter("placement", e.target.value)}
        >
          <option value="">All Placements</option>
          {PLACEMENT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={params.surface || ""}
          onChange={(e) => updateFilter("surface", e.target.value)}
        >
          <option value="">All Surfaces</option>
          {SURFACE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={params.is_active || ""}
          onChange={(e) => updateFilter("is_active", e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      <div className="ds-table-wrapper">
        <table className="ds-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Preview</th>
              <th>Name</th>
              <th>Placement</th>
              <th>Size</th>
              <th>Surface</th>
              <th>Pos</th>
              <th>Link</th>
              <th>Status</th>
              <th>Schedule</th>
              {editable && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={editable ? 11 : 10}
                  style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}
                >
                  Loading banners...
                </td>
              </tr>
            ) : banners.length === 0 ? (
              <tr>
                <td
                  colSpan={editable ? 11 : 10}
                  style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}
                >
                  No banners found. {editable ? "Create your first banner." : ""}
                </td>
              </tr>
            ) : (
              banners.map((b, i) => (
                <tr key={b.id}>
                  <td>{startIndex + i + 1}</td>
                  <td>
                    {b.image ? (
                      <img
                        src={b.image}
                        alt=""
                        className="bn-thumb"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="bn-thumb--empty">
                        <ImageOff size={14} />
                      </div>
                    )}
                  </td>
                  <td>
                    {b.name || (
                      <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                        Unnamed
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`ds-badge ds-badge--placement-${b.placement}`}>
                      {b.placement}
                    </span>
                  </td>
                  <td style={{ textTransform: "capitalize" }}>{b.size}</td>
                  <td>
                    <span className="ds-badge ds-badge--surface">{b.surface}</span>
                  </td>
                  <td>{b.position ?? 0}</td>
                  <td>
                    {b.link_url || b.ar_link_url ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {b.link_url && (
                          <a
                            href={b.link_url}
                            target="_blank"
                            rel="noreferrer"
                            className="bn-link"
                            title={`EN: ${b.link_url}`}
                          >
                            <span>{b.link_url}</span>
                            <ExternalLink size={12} />
                          </a>
                        )}
                        {b.ar_link_url && (
                          <a
                            href={b.ar_link_url}
                            target="_blank"
                            rel="noreferrer"
                            className="bn-link"
                            title={`AR: ${b.ar_link_url}`}
                          >
                            <span>AR: {b.ar_link_url}</span>
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: "#9ca3af" }}>—</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`ds-badge ${
                        b.is_active ? "ds-badge--active" : "ds-badge--inactive"
                      }`}
                    >
                      {b.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <span className="ds-date-range">
                      {formatDateRange(b.starts_at, b.ends_at)}
                    </span>
                  </td>
                  {editable && (
                    <td>
                      <div className="ds-actions">
                        <button
                          className="ds-btn-edit"
                          onClick={() => navigate(`/banners/edit/${b.id}`)}
                          aria-label={`Edit banner ${b.id}`}
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          className="ds-btn-delete"
                          disabled={deletingId === b.id}
                          onClick={() => handleDelete(b)}
                          aria-label={`Delete banner ${b.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {pagination && totalPages > 1 && (
          <div className="ds-pagination">
            <span>
              Page {params.page} of {totalPages} ({pagination.total} banners)
            </span>
            <div className="ds-pagination-buttons">
              <button
                disabled={params.page <= 1}
                onClick={() => changePage(params.page - 1)}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                .filter(
                  (p) => Math.abs(p - params.page) <= 2 || p === 1 || p === totalPages,
                )
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span style={{ padding: "0 4px" }}>...</span>
                    )}
                    <button
                      className={params.page === p ? "active" : ""}
                      onClick={() => changePage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
              <button
                disabled={params.page >= totalPages}
                onClick={() => changePage(params.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BannersList;
